import { FC, useContext, useLayoutEffect, useRef, useState } from 'react';

import { CalendarIcon, Label, Separator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { createPortal } from 'react-dom';

import { LocalizationContext } from 'localization';
import { IHostBooking } from 'types';

import s from './BookingsTooltip.module.scss';

export interface IBookingsTooltipProps {
  bookings: Array<IHostBooking>;
}

export const BookingsTooltip: FC<IBookingsTooltipProps> = ({ bookings }) => {
  const { t } = useContext(LocalizationContext);

  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  const openerRef = useRef<HTMLDivElement>(null);
  const [tooltipContainerEl, setTooltipContainerEl] =
    useState<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    if (!isVisible || !openerRef.current) return;

    const { top, left } = openerRef.current.getBoundingClientRect();

    const el = document.createElement('div');
    el.classList.add(s.tooltip);
    el.style.top = `${top - 8}px`;
    el.style.left = `${left - 8}px`;

    document.body.appendChild(el);
    setTooltipContainerEl(el);

    return () => {
      document.body.removeChild(el);
    };
  }, [isVisible, openerRef.current]);

  useLayoutEffect(() => {
    if (!tooltipContainerEl) return;

    const { top, height } = tooltipContainerEl.getBoundingClientRect();

    if (window.innerHeight <= top + height) {
      tooltipContainerEl.style.top = `${window.innerHeight - height - 8}px`;
    }
  }, [tooltipContainerEl]);

  return (
    <div
      className={s.container}
      ref={openerRef}
      onDoubleClick={(e) => e.stopPropagation()}
      onMouseEnter={() => {
        if (tooltipTimer.current) {
          clearTimeout(tooltipTimer.current);
          tooltipTimer.current = null;
        }

        setIsVisible(true);
      }}
      onMouseLeave={() => {
        tooltipTimer.current = setTimeout(() => setIsVisible(false), 50);
      }}
    >
      <div onClick={() => setIsVisible((arg) => !arg)} className={s.label}>
        <Label text={t('host.statuses.booked')} color="blue" />
      </div>
      {tooltipContainerEl &&
        createPortal(
          <div className={s.tooltipContent}>
            <div className={s.header}>
              <CalendarIcon size="s" />
              {t('host.bookings.nearest')}
            </div>
            <Separator />
            <div className={s.bookings}>
              {bookings.map((booking) => (
                <div key={`booking-${booking.id}`} className={s.row}>
                  <div className={s.date}>
                    {dayjs(booking.from).format('DD.MM.YYYY')}
                  </div>
                  <div className={s.time}>
                    {dayjs(booking.from).format('HH:mm')}
                    <span>→</span>
                    {dayjs(booking.to).format('HH:mm')}
                  </div>
                  <span className={s.client}>
                    {booking.client?.nickname ?? t('client.guest')}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          tooltipContainerEl
        )}
    </div>
  );
};
